import _ from 'lodash';
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { Result } from '../../api/SearchApi';
import { AuthData } from '../../context/auth';
import { ConfigData } from '../../context/config';
import {
	GraceLapseReinstatementResponse,
	Policy2,
	PolicyInvoice2Response,
} from '../../types/policy';
import { capitalize, getPolicyStatus, getReadableName } from '../../util/util';
import {
	CLAIM_OPTION_TITLE,
	POLICY_OPTION_TITLE,
	QUOTE_OPTION_SUBTITLE,
	QUOTE_OPTION_TITLE,
	SEARCH__RESULTS_ENTITY_TYPES,
} from './constants';

export function getEntityValue(
	uri: string,
	properties: any,
	ontologyItems: any[],
	searchResultValues: any,
): string[] {
	return formatProperty(
		getProperty(uri, properties, ontologyItems, searchResultValues),
	);
}

export function getProperty(
	uri: string,
	properties: any,
	ontologyItems: any[],
	searchResultValues: any,
) {
	if (!properties[uri]) {
		const ontologyDefinition = ontologyItems.find((o) => o.uri === uri).type
			.options;
		const values = searchResultValues[uri];
		properties[uri] = { ontologyDefinition, values };
	}

	return properties[uri];
}

export function formatProperty(property: any): string[] {
	return _.compact(
		_.map(property.values, (value: any) => {
			if (_.isNull(value) || _.isUndefined(value)) {
				return null;
			} else if (property.isInvalid) {
				return value ? value.toString() : null;
			} else if (property.ontologyDefinition.type === 'type.date') {
				const date = new Date(value);
				if (!date) {
					return null;
				}
				return date.toString();
			} else {
				return value.toString();
			}
		}),
	);
}

export function getValues(
	key: string,
	properties: any,
	ontologyItems: any[],
	searchResultValues: any,
) {
	const entityUri: string = key;
	return getEntityValue(
		entityUri,
		properties,
		ontologyItems,
		searchResultValues,
	);
}

export function getTemplateResult(
	template: string,
	ontologyItems: any[],
	searchResultValues: any,
): any {
	if (!template) {
		return null;
	}

	const regex: RegExp = /{{([\s\S]+?)}}/g;
	_.templateSettings.interpolate = regex;

	const templateData: { [key: string]: string } = {};
	const properties = {};
	for (
		let matches: string[] = regex.exec(template);
		matches;
		matches = regex.exec(template)
	) {
		const key: string = matches[1].replace(/[.*]/g, '');
		templateData[key] = getValues(
			key,
			properties,
			ontologyItems,
			searchResultValues,
		)[0];
	}
	return _.template(template)(templateData);
}

export function isPolicy2SearchResult(result: any): boolean {
	return result.entityType === 'entity.policy2' && !!result.policy2;
}

function hasOverdueInvoices({ invoices }: Policy2): boolean {
	return invoices.some(isOverdueInvoice);
}

function isOverdueInvoice({
	dueTimestamp,
	settlementStatus,
}: PolicyInvoice2Response) {
	return (
		_.parseInt(dueTimestamp) < Date.now() && settlementStatus === 'outstanding'
	);
}

export function policiesPossiblyInGrace(results: Result[]) {
	return results
		.filter(isPolicy2SearchResult)
		.filter((result) => hasOverdueInvoices(result.policy2))
		.map((result) => result.policy2);
}

export function getCurrentGraceLapse(
	graceLapseReinstatements: GraceLapseReinstatementResponse[],
): GraceLapseReinstatementResponse {
	if (graceLapseReinstatements) {
		const currentGraceLapse = _.find(
			graceLapseReinstatements,
			(gp) => !gp.gracePeriod.settledTimestamp,
		);

		return currentGraceLapse;
	} else {
		return null;
	}
}

export function getCurrentGracePeriodPolicyLocator(gracePeriods: any[]) {
	if (gracePeriods) {
		const currentGracePeriod = _.find(
			gracePeriods,
			(gp) => !gp.settledTimestamp,
		);
		return currentGracePeriod ? currentGracePeriod.policyLocator : null;
	} else {
		return null;
	}
}

export const getPolicyholderData = (
	searchResult: Result,
	configData: ConfigData,
) => {
	const policyholderCardDefinition =
		configData.policyholderCardConfig.workflows.find(
			(w: any) => w.message === 'policyHolderSearch.json',
		).value;
	const title1 = getTemplateResult(
		policyholderCardDefinition.title,
		configData.ontologyData.items,
		searchResult.entity.values,
	);
	const title2 = getTemplateResult(
		policyholderCardDefinition.subtitle,
		configData.ontologyData.items,
		searchResult.entity.values,
	);
	const subtitle1 = getTemplateResult(
		policyholderCardDefinition.tag,
		configData.ontologyData.items,
		searchResult.entity.values,
	);
	const value = `/policyholder/${searchResult.entity.id}`;
	const policyholderLabel = [title2, subtitle1].filter((v) => !!v).join(' ');
	const label = `${title1} - ${policyholderLabel}`;
	const type = 'policyholder';

	return { value, label, title1, title2, subtitle1, type };
};

export const getPolicyOption = (searchResult: any, authData: AuthData) => {
	const policy = searchResult.policy2;
	const graceStartTimestamp = searchResult.gracePeriodStart;
	const {
		productName,
		displayId,
		originalContractStartTimestamp,
		effectiveContractEndTimestamp,
	} = policy;
	const title1 = `${getReadableName(
		productName,
	)} ${POLICY_OPTION_TITLE} ${displayId}`;
	const title2 = capitalize(getPolicyStatus(policy, null, graceStartTimestamp));
	const subtitle1 = authData.getFormattedDate(originalContractStartTimestamp);
	const subtitle2 = authData.getFormattedDate(effectiveContractEndTimestamp);
	const value = `/policy/${displayId}`;
	const label = `${title1}, ${subtitle1} - ${title2}, ${subtitle2}`;
	const type = 'policy';

	return { value, label, title1, title2, subtitle1, subtitle2, type };
};

export const getClaimOption = (searchResult: any, authData: AuthData) => {
	const claim = searchResult.claim3;
	const {
		productName,
		policyLocator,
		locator,
		currentStatus,
		notificationTimestamp,
		incidentTimestamp,
	} = claim;
	const title1 = `${getReadableName(
		productName,
	)} ${CLAIM_OPTION_TITLE} ${locator}`;
	const title2 = capitalize(currentStatus);
	const subtitle1 = notificationTimestamp
		? authData.getFormattedDate(notificationTimestamp)
		: '--';
	const subtitle2 = incidentTimestamp
		? authData.getFormattedDate(incidentTimestamp)
		: '--';
	const value = `/policy/${policyLocator}/claims/${locator}`;
	const label = `${title1}, ${subtitle1} - ${title2}, ${subtitle2}`;
	const type = 'claim';

	return { value, label, title1, title2, subtitle1, subtitle2, type };
};

export const getQuoteOption = (searchResult: any) => {
	const quote = searchResult.policyQuote;
	const { name, state, policyLocator, locator } = quote;
	const title1 = `${QUOTE_OPTION_TITLE} ${locator}`;
	const title2 = capitalize(state);
	const subtitle1 = `${QUOTE_OPTION_SUBTITLE} ${name}`;
	const value = `/policy/${policyLocator}/quotes/${locator}`;
	const label = `${title1}, ${subtitle1} - ${title2}`;
	const type = 'quote';

	return { value, label, title1, title2, subtitle1, locator, type };
};

export const getSearchResultData = (
	searchResult: Result,
	configData: ConfigData,
	authData: AuthData,
) => {
	switch (searchResult.entityType) {
		case SEARCH__RESULTS_ENTITY_TYPES.POLICYHOLDER:
			return getPolicyholderData(searchResult, configData);
		case SEARCH__RESULTS_ENTITY_TYPES.POLICY:
			return getPolicyOption(searchResult, authData);
		case SEARCH__RESULTS_ENTITY_TYPES.CLAIM:
			return getClaimOption(searchResult, authData);
		case SEARCH__RESULTS_ENTITY_TYPES.QUOTE:
			return getQuoteOption(searchResult);
		default:
			return null;
	}
};

export const useArrowUpOrDown = (
	arrowUpCallBack: (e?: any) => void,
	arrowDownCallBack: (e?: any) => void,
	node = null as any,
) => {
	// implement the callback ref pattern
	const arrowUpCallBackRef = useRef(arrowUpCallBack);
	const arrowDownCallBackRef = useRef(arrowDownCallBack);
	useLayoutEffect(() => {
		arrowUpCallBackRef.current = arrowUpCallBack;
		arrowDownCallBackRef.current = arrowDownCallBack;
	});

	// handle what happens on key press
	const handleKeyPress = useCallback((event) => {
		// Arrow down
		if (event.keyCode === 40) {
			arrowDownCallBackRef.current(event);
		}
		// Arrow up
		if (event.keyCode === 38) {
			arrowUpCallBackRef.current(event);
		}
	}, []);

	useEffect(() => {
		// target is either the provided node or the document
		const targetNode = node ?? document;
		// attach the event listener
		targetNode && targetNode.addEventListener('keydown', handleKeyPress);

		// remove the event listener
		return () =>
			targetNode && targetNode.removeEventListener('keydown', handleKeyPress);
	}, [handleKeyPress, node]);
};

// see https://github.com/tannerlinsley/react-query/issues/293
// see https://usehooks.com/useDebounce/
export default function useDebounce(value: string, delay: number) {
	// State and setters for debounced value
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(
		() => {
			// Update debounced value after delay
			const handler = setTimeout(() => {
				setDebouncedValue(value);
			}, delay);

			// Cancel the timeout if value changes (also on delay change or unmount)
			// This is how we prevent debounced value from updating if value is changed ...
			// .. within the delay period. Timeout gets cleared and restarted.
			return () => {
				clearTimeout(handler);
			};
		},
		[value, delay], // Only re-call effect if value or delay changes
	);

	return debouncedValue;
}
